import React, { useState, useRef, useEffect } from "react";
import PageLayout from "../components/PageLayout";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS, DEMO_RISKS, DEMO_PORTFOLIO_KPIs } from "../demoData";
import axios from "axios";

const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : `$${(n/1000).toFixed(0)}K`;

const SUGGESTED_QUERIES = [
  "What is the overall portfolio health status?",
  "Which projects are most at risk of failure?",
  "What are the top 3 risks requiring executive attention?",
  "How is the portfolio performing against budget?",
  "Which PMs need coaching or intervention?",
  "What is the benefits realization forecast for 2026?",
  "Summarize the critical path risks across all projects.",
  "Give me a board-ready executive summary.",
  "Which projects should be killed or redirected?",
  "What resources are bottlenecks across the portfolio?",
];

function buildSystemContext() {
  const kpi = DEMO_PORTFOLIO_KPIs;
  const ragSummary = `${kpi.onTrack} On Track, ${kpi.atRisk} At Risk, ${kpi.failing} Failing`;
  const topRisks = DEMO_RISKS.slice(0, 3).map(r => `${r.id}: ${r.title} (Score: ${r.score.toFixed(1)})`).join("; ");
  const critProj = DEMO_PROJECTS.filter(p => p.rag === "red").map(p => `${p.id} (${p.name}, Fail: ${p.failProb}%)`).join(", ");
  return `You are the Shuriken PMO Intelligent Platform AI Agent — an enterprise-grade PMO intelligence assistant. You have access to live portfolio data and ML predictions.

CURRENT PORTFOLIO SNAPSHOT (as at 2026-05-11):
- Total projects: ${kpi.totalProjects}
- Portfolio budget: ${fmt(kpi.totalBudget)} | Spent: ${fmt(kpi.totalSpent)}
- RAG Status: ${ragSummary}
- Average CPI: ${kpi.avgCPI} | Average SPI: ${kpi.avgSPI}
- Average failure probability: ${kpi.avgFailProb}%
- Benefits planned: ${fmt(kpi.totalBenefitsPlanned)} | Projected: ${fmt(kpi.totalBenefitsProjected)}
- PM data quality: ${kpi.pmDataQuality}%
- Critical risks: ${topRisks}
- Failing projects: ${critProj || "None"}

You are powered by Claude claude-sonnet-4-6. Respond with precise, actionable PMO intelligence. Use data from the portfolio context above. Format responses clearly with headings where appropriate. Always prioritize executive-ready language.`;
}

export default function AIAgentConsole() {
  const { theme } = useTheme();
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [apiKey, setApiKey]       = useState(localStorage.getItem("pmo_api_key") || "");
  const [showKeyForm, setShowKeyForm] = useState(!localStorage.getItem("pmo_api_key"));
  const [model, setModel]         = useState("claude-sonnet-4-6");
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const saveApiKey = () => {
    localStorage.setItem("pmo_api_key", apiKey);
    setShowKeyForm(false);
    setMessages([{
      role: "assistant",
      content: "✦ Shuriken PMO AI Agent connected. I have full access to your portfolio data, ML predictions, risk scores, and EVM metrics. How can I assist you?",
      ts: new Date().toLocaleTimeString(),
    }]);
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim(), ts: new Date().toLocaleTimeString() };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      // Use backend proxy to avoid CORS and key exposure
      const response = await axios.post("/api/ai-agent", {
        messages: [...messages, userMsg].map(m => ({ role: m.role, content: m.content })),
        system: buildSystemContext(),
        model,
        api_key: apiKey,
      });

      const assistantMsg = {
        role: "assistant",
        content: response.data.content || "No response received.",
        ts: new Date().toLocaleTimeString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      // Fallback: generate intelligent demo responses
      const demoResponse = generateDemoResponse(userMsg.content);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: demoResponse,
        ts: new Date().toLocaleTimeString(),
        isDemo: true,
      }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <PageLayout
      title="AI Agent Console"
      subtitle="Natural language portfolio queries powered by Claude AI — P-12"
      actions={
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <select value={model} onChange={e => setModel(e.target.value)}
            style={{ padding: "5px 10px", fontSize: "11px" }}>
            <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
            <option value="claude-opus-4-7">Claude Opus 4.7</option>
            <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5</option>
          </select>
          <button onClick={() => setShowKeyForm(!showKeyForm)} className="btn-secondary" style={{ padding: "5px 12px", fontSize: "11px" }}>
            {apiKey ? "⚙ API Key" : "🔑 Set API Key"}
          </button>
        </div>
      }
    >
      {/* API Key Setup */}
      {showKeyForm && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: theme.accent }}>
          <div className="section-title">Anthropic API Configuration</div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              style={{ flex: 1 }}
            />
            <button onClick={saveApiKey} className="btn-primary">CONNECT</button>
            <button onClick={() => setShowKeyForm(false)} className="btn-secondary">DEMO MODE</button>
          </div>
          <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "8px" }}>
            API key stored in browser localStorage only. Not transmitted to any server except Anthropic.
            Without a key, the agent operates in intelligent demo mode using portfolio data.
          </div>
        </div>
      )}

      {/* Suggested Queries */}
      {messages.length === 0 && (
        <div className="pmo-card" style={{ marginBottom: "16px" }}>
          <div className="section-title">Suggested Portfolio Queries</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
            {SUGGESTED_QUERIES.map((q, i) => (
              <button key={i} onClick={() => setInput(q)}
                style={{
                  padding: "6px 12px", borderRadius: "16px",
                  border: `1px solid ${theme.cardBorder}`,
                  background: "transparent", color: theme.textSecondary,
                  cursor: "pointer", fontSize: "11px",
                  transition: "all 0.15s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.accent; e.currentTarget.style.color = theme.accent; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.cardBorder; e.currentTarget.style.color = theme.textSecondary; }}
              >{q}</button>
            ))}
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div style={{
        height: "calc(100vh - 380px)", minHeight: "300px",
        overflow: "auto",
        display: "flex", flexDirection: "column", gap: "12px",
        padding: "4px 0", marginBottom: "14px",
      }}>
        {messages.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px", color: theme.textMuted }}>
            <div style={{ fontSize: "40px", marginBottom: "12px", color: theme.accent, opacity: 0.5 }}>✦</div>
            <div style={{ fontFamily: "'Orbitron',monospace", fontSize: "13px", letterSpacing: "2px", marginBottom: "8px" }}>
              SHURIKEN PMO AI AGENT
            </div>
            <div style={{ fontSize: "12px" }}>
              Ask any portfolio question in natural language. The AI has full access to your project data, ML predictions, and EVM metrics.
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex",
            justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            animation: "fadeInUp 0.3s ease forwards",
          }}>
            <div style={{
              maxWidth: "75%",
              padding: "12px 16px",
              borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              background: msg.role === "user"
                ? `linear-gradient(135deg, ${theme.accent}, ${theme.accentDim})`
                : theme.card,
              border: msg.role === "user" ? "none" : `1px solid ${theme.cardBorder}`,
              color: msg.role === "user" ? "#000" : theme.textSecondary,
            }}>
              {msg.role === "assistant" && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px" }}>
                  <span style={{ color: theme.accent, fontSize: "12px" }}>✦</span>
                  <span style={{ fontFamily: "'Orbitron',monospace", fontSize: "9px", color: theme.accent, letterSpacing: "1px" }}>
                    SHURIKEN PMO AI {msg.isDemo ? "· DEMO MODE" : ""}
                  </span>
                  <span style={{ fontSize: "9px", color: theme.textMuted, marginLeft: "auto" }}>{msg.ts}</span>
                </div>
              )}
              <div style={{ fontSize: "13px", lineHeight: "1.6", whiteSpace: "pre-wrap" }}>
                {msg.content}
              </div>
              {msg.role === "user" && (
                <div style={{ fontSize: "9px", color: "rgba(0,0,0,0.5)", textAlign: "right", marginTop: "4px" }}>{msg.ts}</div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "12px 16px", background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "16px 16px 16px 4px" }}>
              <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                <span style={{ color: theme.accent, fontSize: "10px" }}>✦ Analyzing portfolio</span>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: "4px", height: "4px", borderRadius: "50%", background: theme.accent, animation: `pulse-glow 1s ease-in-out ${i * 0.2}s infinite` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input Area */}
      <div style={{
        display: "flex", gap: "10px", alignItems: "flex-end",
        padding: "12px", background: theme.card,
        border: `1px solid ${theme.cardBorder}`,
        borderRadius: "12px",
      }}>
        <textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your portfolio... (Enter to send, Shift+Enter for new line)"
          rows={2}
          style={{
            flex: 1, resize: "none", background: "transparent",
            border: "none", outline: "none",
            color: theme.textPrimary, fontSize: "13px", lineHeight: "1.5",
            fontFamily: "'Exo 2', sans-serif",
          }}
        />
        <div style={{ display: "flex", gap: "6px" }}>
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="btn-secondary"
              style={{ padding: "8px 12px", fontSize: "11px" }}
            >CLEAR</button>
          )}
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="btn-primary"
            style={{ padding: "10px 20px", fontSize: "12px", opacity: loading || !input.trim() ? 0.5 : 1 }}
          >
            {loading ? "..." : "SEND ▶"}
          </button>
        </div>
      </div>
    </PageLayout>
  );
}

function generateDemoResponse(query) {
  const q = query.toLowerCase();
  const kpi = DEMO_PORTFOLIO_KPIs;

  if (q.includes("health") || q.includes("status")) {
    return `## Portfolio Health Summary\n\nThe portfolio of **${kpi.totalProjects} active projects** is currently showing:\n\n• **${kpi.onTrack} On Track** — Performing within acceptable parameters\n• **${kpi.atRisk} At Risk** — Require attention and mitigation\n• **${kpi.failing} Failing** — Immediate intervention required\n\nAverage CPI: **${kpi.avgCPI}** | Average SPI: **${kpi.avgSPI}**\n\nThe portfolio composite health is **AMBER** — below target performance threshold. Primary drivers are schedule slippage in P003, P007, and P012, combined with cost overruns in the Digital Transformation programme.\n\n*Recommendation: Schedule an emergency portfolio review for red-status projects within 48 hours.*`;
  }
  if (q.includes("risk") && (q.includes("top") || q.includes("executive"))) {
    return `## Top 5 Portfolio Risks — Board Alert\n\n**R001 (CRITICAL) — Cybersecurity Framework Over Budget**\nScore: 70.2 | Exposure: $220K | Velocity: RISING\n→ Recommend immediate scope reduction\n\n**R002 (CRITICAL) — HR System Go-Live Delay**\nScore: 61.2 | Exposure: $180K | Velocity: RISING\n→ Engage additional implementation consultant\n\n**R003 (HIGH) — Network Upgrade Cost Overrun**\nScore: 55.8 | Exposure: $150K | Velocity: STABLE\n→ Negotiate vendor contract amendment\n\n**R004 (HIGH) — Analytics Platform Scope Creep**\nScore: 44.0 | Exposure: $400K | Velocity: RISING\n→ Enforce formal change control\n\n**R005 (HIGH) — ERP Key Resource Availability**\nScore: 42.5 | Exposure: $320K | Velocity: STABLE\n→ Cross-train backup and lock key staff\n\n*Total portfolio risk exposure: $1.29M*`;
  }
  if (q.includes("budget") || q.includes("financial") || q.includes("cost")) {
    return `## Financial Performance Summary\n\n**Portfolio Budget:** $24.3M approved\n**Total Spent:** $12.8M (53% consumed)\n**Average CPI:** ${kpi.avgCPI} — **BELOW TARGET**\n\nKey financial concerns:\n• P003 (Cybersecurity): CPI 0.82 — significantly over budget\n• P007 (HR Digital): CPI 0.78 — critical overrun\n• P012 (Network): CPI 0.81 — near budget exhaustion\n\nPositive performers:\n• P006 (Supply Chain): CPI 1.12\n• P010 (AI/ML Platform): CPI 1.08\n• P002 (Cloud Migration): CPI 1.07\n\n*Forecast at Completion (EAC) indicates a portfolio-level overrun of approximately $1.2M if current CPI trends continue.*`;
  }
  if (q.includes("board") || q.includes("executive") || q.includes("summary")) {
    return `## Executive Board Summary — ${new Date("2026-05-11").toLocaleDateString("en-US", { month: "long", year: "numeric" })}\n\nThe portfolio is performing at CPI ${kpi.avgCPI} and SPI ${kpi.avgSPI} against plan. **3 of 12 projects are in failing status**, requiring board-level decisions this quarter.\n\n**3 Decisions Required:**\n1. P003 Cybersecurity — Approve scope reduction (saves ~$220K)\n2. P007 HR Digital — Authorize PM replacement or co-PM\n3. P010 AI/ML — Approve $800K investment acceleration\n\n**Financial:** $24.3M portfolio, 53% consumed. Projected overrun of ~$1.2M.\n**Benefits:** $19.75M projected vs $21.05M planned — gap of $1.3M requiring remediation.\n**PM Quality:** Portfolio average 80%. 3 PMs scoring below 65% threshold.\n\n*AI Confidence: High (based on ensemble RF + XGB + LightGBM models, 98.2% accuracy)*`;
  }
  if (q.includes("pm") || q.includes("workforce") || q.includes("coach")) {
    return `## PM Workforce Quality Assessment\n\nPortfolio average PM score: **80%** (target: 85%)\n\n**Immediate Action Required:**\n• Carlos Silva (P007): Score 56% — Delivery predictability at 48%. Schedule slip 55 days. Recommend: Co-PM assignment or replacement.\n• Maya Patel (P003): Score 61% — Data integrity issues. Recommend: Bi-weekly PMO check-in + data quality workshop.\n• Elena Kowalski (P012): Score 64% — Declining trend. Recommend: Performance review + coaching plan.\n\n**Top Performers:**\n• Fatima Al-Zahra (P006): 97% — Consider for mentorship role\n• Zara Khan (P010): 96% — Eligible for advanced portfolio\n• Ahmed Hassan (P002): 94% — Strong technical delivery\n\n*3 PMs below threshold are managing projects with combined budget of $4.8M.*`;
  }
  if (q.includes("kill") || q.includes("redirect") || q.includes("rationali")) {
    return `## Portfolio Rationalization Recommendations\n\n**KILL (2 projects):**\n• P003 (Cybersecurity Framework): 78% fail probability, CPI 0.82, significant budget overrun. Estimated savings: $150K remaining budget.\n• P007 (HR Digital): 78% fail probability, CPI 0.78. Recommend terminate and restart with new approach in Q1 2027.\n\n**REDIRECT (2 projects):**\n• P001 (ERP): CPI 0.89, SPI 0.94. Recommend scope reduction — defer Phase 3 modules.\n• P012 (Network Upgrade): CPI 0.81, 93% complete. Push remaining work to maintain budget.\n\n**INVEST+ (2 projects):**\n• P010 (AI/ML Platform): CPI 1.08, 10% fail risk. ROI projected at 140% — accelerate.\n• P006 (Supply Chain): CPI 1.12, ROI 179%. Explore early completion bonus.\n\n*Potential savings from kill/redirect decisions: ~$2.1M for reallocation.*`;
  }
  return `## Portfolio Intelligence Response\n\nBased on current portfolio data (as at 2026-05-11):\n\n${DEMO_PROJECTS.slice(0, 3).map(p => `• **${p.name}** (${p.id}): CPI ${p.cpi.toFixed(2)}, ${p.failProb}% failure risk — ${p.rag.toUpperCase()}`).join("\n")}\n\nFor a more specific analysis, try asking about:\n• "Which projects are most at risk?"\n• "Give me a financial performance summary"\n• "What decisions require board attention?"\n\n*Shuriken PMO AI is operating in demo mode. Connect your Anthropic API key for full Claude AI responses.*`;
}
